import React, { useState } from 'react';

// Material-UI
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    CircularProgress,
    Typography,
    TextField,
    Paper,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { styled, useTheme } from '@mui/material/styles';

// Components
import { ActionButton } from 'components/ui/buttons';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';
import InfoToolTip from 'components/popups/InfoTooltip';
import FullScreenModal from 'components/popups/FullScreenModal';
import { useNotification } from 'context/notification';
import useExpresso from 'hooks/useExpresso';
import fetchExpresso from 'utility/fetchExpresso';
import { validateURL } from 'utility/validation';

export default function SocialMediaLinks(props) {
    const [refresh, setRefresh] = useState(0);
    const [host, loadingHost, error] = useExpresso(
        `/apiv2/hosts/${props.hostId}`,
        'GET',
        null,
        null,
        [refresh],
    );

    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const { notify } = useNotification();

    // RETURN: Loading
    if (loadingHost) {
        return (
            <CardRoot>
                <CardHeader title="Social Media Links" />
                <Box flexGrow={1}>
                    <Box
                        display="flex"
                        height="100%"
                        width="100%"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <CircularProgress />
                    </Box>
                </Box>
            </CardRoot>
        );
    }

    // RETURN: Error
    if (error || !host) {
        return (
            <CardRoot>
                <CardHeader title="Social Media Links" />
                <Box flexGrow={1}>
                    <Box
                        display="flex"
                        height="100%"
                        width="100%"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <p>There was a problem getting your social media links</p>
                    </Box>
                </Box>
            </CardRoot>
        );
    }

    const {
        url_website,
        url_facebook,
        url_twitter,
        url_instagram,
        url_tiktok,
        url_linkedin,
        url_youtube,
    } = host;

    const handleModalClose = () => {
        setModalOpen(false);
    };

    const handleUpdate = (website, facebook, twitter, instagram, tiktok, linkedin, youtube) => {
        if (loading) return;
        setLoading(true);

        fetchExpresso(`/apiv2/hosts/${props.hostId}/socials`, {
            method: 'PUT',
            body: {
                website: website.trim() || '',
                facebook: facebook.trim() || '',
                twitter: twitter.trim() || '',
                instagram: instagram.trim() || '',
                tiktok: tiktok.trim() || '',
                linkedin: linkedin.trim() || '',
                youtube: youtube.trim() || '',
            },
        })
            .then((res) => {
                if (res.status === 200) {
                    setModalOpen(false);
                    setLoading(false);
                    setRefresh(refresh + 1);
                } else {
                    throw new Error();
                }
            })
            .catch((e) => {
                notify.error('There was a problem updating your socials');
                setLoading(false);
            });
    };

    return (
        <CardRoot>
            <CardHeaderRoot
                title="Social Media Links"
                action={
                    <InfoToolTip message="These links are displayed on your public Event Listings and Event Ticket pages." />
                }
            />

            <CardBody>
                <Box display="flex" alignItems="center">
                    <WebsiteIcon disabled={!url_website} />
                    {url_website ? (
                        <SocialLink
                            href={url_website}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Typography>{url_website}</Typography>
                        </SocialLink>
                    ) : (
                        <Typography variant="caption">Not set</Typography>
                    )}
                </Box>

                <Box display="flex" alignItems="center">
                    <FacebookIconStyled disabled={!url_facebook} />
                    {url_facebook ? (
                        <SocialLink
                            href={url_facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Typography>{url_facebook}</Typography>
                        </SocialLink>
                    ) : (
                        <Typography variant="caption">Not set</Typography>
                    )}
                </Box>

                <Box display="flex" alignItems="center">
                    <InstagramIconStyled disabled={!url_instagram} />
                    {url_instagram ? (
                        <SocialLink
                            href={url_instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Typography>{url_instagram}</Typography>
                        </SocialLink>
                    ) : (
                        <Typography variant="caption">Not set</Typography>
                    )}
                </Box>

                <Box display="flex" alignItems="center">
                    <SocialImage
                        alt=""
                        src={
                            url_twitter
                                ? process.env.REACT_APP_PORTAL_S3_LOCATION + '/x-icon-black.svg'
                                : process.env.REACT_APP_PORTAL_S3_LOCATION + '/x-icon-grey.svg'
                        }
                        variant="twitter"
                    />
                    {url_twitter ? (
                        <SocialLink
                            href={url_twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Typography>{url_twitter}</Typography>
                        </SocialLink>
                    ) : (
                        <Typography variant="caption">Not set</Typography>
                    )}
                </Box>

                <Box display="flex" alignItems="center">
                    <SocialImage
                        alt=""
                        src={
                            url_tiktok
                                ? process.env.REACT_APP_PORTAL_S3_LOCATION +
                                '/tiktok-icon-black.svg'
                                : process.env.REACT_APP_PORTAL_S3_LOCATION + '/tiktok-icon-grey.svg'
                        }
                        variant="tiktok"
                    />
                    {url_tiktok ? (
                        <SocialLink
                            href={url_tiktok}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Typography>{url_tiktok}</Typography>
                        </SocialLink>
                    ) : (
                        <Typography variant="caption">Not set</Typography>
                    )}
                </Box>

                <Box display="flex" alignItems="center">
                    <LinkedInIconStyled disabled={!url_linkedin} />
                    {url_linkedin ? (
                        <SocialLink
                            href={url_linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Typography>{url_linkedin}</Typography>
                        </SocialLink>
                    ) : (
                        <Typography variant="caption">Not set</Typography>
                    )}
                </Box>

                <Box display="flex" alignItems="center">
                    <YouTubeIconStyled disabled={!url_youtube} />
                    {url_youtube ? (
                        <SocialLink
                            href={url_youtube}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Typography>{url_youtube}</Typography>
                        </SocialLink>
                    ) : (
                        <Typography variant="caption">Not set</Typography>
                    )}
                </Box>
            </CardBody>

            <CardActions>
                <ActionButton onClick={() => setModalOpen(true)}>Edit</ActionButton>
            </CardActions>

            <FullScreenModal open={modalOpen} onClose={handleModalClose} title="Update Socials">
                <SocialLinkForm
                    onUpdate={handleUpdate}
                    onCancel={handleModalClose}
                    website={url_website}
                    facebook={url_facebook}
                    twitter={url_twitter}
                    instagram={url_instagram}
                    tiktok={url_tiktok}
                    linkedin={url_linkedin}
                    youtube={url_youtube}
                    loading={loading}
                />
            </FullScreenModal>
        </CardRoot>
    );
}

function SocialLinkForm(props) {
    // Props
    const { onUpdate, onCancel, loading, hostId } = props;

    // State - Form Values
    const [website, setWebsite] = useState(props.website || '');
    const [facebook, setFacebook] = useState(props.facebook || '');
    const [twitter, setTwitter] = useState(props.twitter || '');
    const [instagram, setInstagram] = useState(props.instagram || '');
    const [tiktok, setTiktok] = useState(props.tiktok || '');
    const [linkedin, setLinkedin] = useState(props.linkedin || '');
    const [youtube, setYoutube] = useState(props.youtube || '');

    // Error States
    const [websiteError, setWebsiteError] = useState(false);
    const [facebookError, setFacebookError] = useState(false);
    const [twitterError, setTwitterError] = useState(false);
    const [instagramError, setInstagramError] = useState(false);
    const [tiktokError, setTiktokError] = useState(false);
    const [linkedinError, setLinkedinError] = useState(false);
    const [youtubeError, setYoutubeError] = useState(false);

    // Hooks
    const theme = useTheme();

    function handleSubmit() {
        let error = false;

        setWebsiteError(false);
        setFacebookError(false);
        setTwitterError(false);
        setInstagramError(false);
        setTiktokError(false);
        setLinkedinError(false);
        setYoutubeError(false);

        if (validateURL(website) === false && website.trim() !== '') {
            setWebsiteError(true);
            error = true;
        }
        if (validateURL(facebook) === false && facebook.trim() !== '') {
            setFacebookError(true);
            error = true;
        }
        if (validateURL(twitter) === false && twitter.trim() !== '') {
            setTwitterError(true);
            error = true;
        }
        if (validateURL(instagram) === false && instagram.trim() !== '') {
            setInstagramError(true);
            error = true;
        }
        if (validateURL(tiktok) === false && tiktok.trim() !== '') {
            setTiktokError(true);
            error = true;
        }
        if (validateURL(linkedin) === false && linkedin.trim() !== '') {
            setLinkedinError(true);
            error = true;
        }
        if (validateURL(youtube) === false && youtube.trim() !== '') {
            setYoutubeError(true);
            error = true;
        }

        if (error) return;

        onUpdate(
            website.trim(),
            facebook.trim(),
            twitter.trim(),
            instagram.trim(),
            tiktok.trim(),
            linkedin.trim(),
            youtube.trim(),
        );
    }

    return (
        <Box width={1} display="flex" flexDirection="column" alignItems="center">
            <Box
                width={[0.9, 0.9, theme.breakpoints.values.md - 60]}
                my={6}
                display="flex"
                justifyContent={hostId ? 'space-between' : 'center'}
                flexWrap="wrap"
            >
                <Box width={[1, 1, 0.6]} display="flex" flexDirection="column">
                    <FormContainer>
                        <TextField
                            type="url"
                            variant="outlined"
                            margin="normal"
                            label="Website URL"
                            placeholder="https://example.com"
                            error={websiteError}
                            helperText={
                                websiteError
                                    ? 'Make sure your link is properly formatted. Example: https://example.com '
                                    : undefined
                            }
                            value={website}
                            onChange={(e) => setWebsite(e.target.value.slice(0, 200))}
                        />

                        <TextField
                            type="url"
                            variant="outlined"
                            margin="normal"
                            label="Facebook URL"
                            placeholder="https://facebook.com/example"
                            error={facebookError}
                            helperText={
                                facebookError
                                    ? 'Make sure your link is properly formatted. Example: https://facebook.com/example'
                                    : undefined
                            }
                            value={facebook}
                            onChange={(e) => setFacebook(e.target.value.slice(0, 200))}
                        />

                        <TextField
                            type="url"
                            variant="outlined"
                            margin="normal"
                            label="Instagram URL"
                            placeholder="https://instagram.com/example"
                            error={instagramError}
                            helperText={
                                instagramError
                                    ? 'Make sure your link is properly formatted. Example: https://instagram.com/example'
                                    : undefined
                            }
                            value={instagram}
                            onChange={(e) => setInstagram(e.target.value.slice(0, 200))}
                        />

                        <TextField
                            type="url"
                            variant="outlined"
                            margin="normal"
                            label="X URL"
                            placeholder="https://x.com/example"
                            error={twitterError}
                            helperText={
                                twitterError
                                    ? 'Make sure your link is properly formatted. Example: https://x.com/example'
                                    : undefined
                            }
                            value={twitter}
                            onChange={(e) => setTwitter(e.target.value.slice(0, 200))}
                        />

                        <TextField
                            type="url"
                            variant="outlined"
                            margin="normal"
                            label="Tiktok URL"
                            placeholder="https://tiktok.com/@example"
                            error={tiktokError}
                            helperText={
                                tiktokError
                                    ? 'Make sure your link is properly formatted. Example: https://tiktok.com/@example'
                                    : undefined
                            }
                            value={tiktok}
                            onChange={(e) => setTiktok(e.target.value.slice(0, 200))}
                        />

                        <TextField
                            type="url"
                            variant="outlined"
                            margin="normal"
                            label="LinkedIn URL"
                            placeholder="https://linkedin.com/company/example"
                            error={linkedinError}
                            helperText={
                                linkedinError
                                    ? 'Make sure your link is properly formatted. Example: https://linkedin.com/company/example'
                                    : undefined
                            }
                            value={linkedin}
                            onChange={(e) => setLinkedin(e.target.value.slice(0, 200))}
                        />

                        <TextField
                            type="url"
                            variant="outlined"
                            margin="normal"
                            label="YouTube URL"
                            placeholder="https://youtube.com/company/example"
                            error={youtubeError}
                            helperText={
                                youtubeError
                                    ? 'Make sure your link is properly formatted. Example: https://youtube.com/channel/example'
                                    : undefined
                            }
                            value={youtube}
                            onChange={(e) => setYoutube(e.target.value.slice(0, 200))}
                        />

                        <Box display="flex" justifyContent="flex-end" mb={2} mt={2}>
                            <CancelButton onClick={onCancel}>Cancel</CancelButton>
                            <SubmitButton onClick={handleSubmit} disabled={loading}>
                                Save
                            </SubmitButton>
                        </Box>
                    </FormContainer>
                </Box>
            </Box>
        </Box>
    );
}

const FormContainer = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
}));

const CardRoot = styled(Card)({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
});

const CardBody = styled(CardContent)({
    flex: 1,
});

const CardHeaderRoot = styled(CardHeader)({
    '& .MuiCardHeader-action': {
        marginTop: 0,
        marginRight: 0,
    },
});

const WebsiteIcon = styled(LanguageIcon, {
    shouldForwardProp: (prop) => prop !== 'disabled',
})(({ theme }) => ({
    fontSize: 28,
    paddingRight: 10,
    paddingTop: 5,
    paddingBottom: 5,
    color: theme.palette.grey[500],
}));

const FacebookIconStyled = styled(FacebookIcon, {
    shouldForwardProp: (prop) => prop !== 'disabled',
})(({ theme, disabled }) => ({
    fontSize: 28,
    paddingRight: 10,
    paddingTop: 5,
    paddingBottom: 5,
    color: disabled ? theme.palette.grey[500] : '#3b5998',
}));

const InstagramIconStyled = styled(InstagramIcon, {
    shouldForwardProp: (prop) => prop !== 'disabled',
})(({ theme, disabled }) => ({
    fontSize: 28,
    paddingRight: 10,
    paddingTop: 5,
    paddingBottom: 5,
    color: disabled ? theme.palette.grey[500] : '#E4405F',
}));

const LinkedInIconStyled = styled(LinkedInIcon, {
    shouldForwardProp: (prop) => prop !== 'disabled',
})(({ theme, disabled }) => ({
    fontSize: 28,
    paddingRight: 10,
    paddingTop: 5,
    paddingBottom: 5,
    color: disabled ? theme.palette.grey[500] : 'rgb(51,117,178)',
}));

const YouTubeIconStyled = styled(YouTubeIcon, {
    shouldForwardProp: (prop) => prop !== 'disabled',
})(({ theme, disabled }) => ({
    fontSize: 28,
    paddingRight: 10,
    paddingTop: 5,
    paddingBottom: 5,
    color: disabled ? theme.palette.grey[500] : 'rgb(219,56,38)',
}));

const SocialLink = styled('a')({
    wordWrap: 'break-word',
    color: '#000',
});

const SocialImage = styled('img', {
    shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant }) => ({
    paddingTop: 5,
    paddingBottom: 5,
    ...(variant === 'twitter' && {
        fontSize: 26,
        paddingRight: 12,
        paddingLeft: 1,
        height: 26,
    }),
    ...(variant === 'tiktok' && {
        fontSize: 28,
        paddingRight: 10,
        height: 28,
    }),
}));
